import {useTranslation} from 'react-i18next'
import {SectionCard} from '@/components/section-card'
import {Tag} from '@/components/ui/tag'
import type {AccionesPlan, FactorPlan} from '@/hooks/bateria/use-bateria-informe'
import {NIVEL_TONE} from '@/lib/bateria'

const FRENTES: {key: keyof AccionesPlan; labelKey: string}[] = [
  {key: 'fuente', labelKey: 'admin.bateria.informe.plan.frenteFuente'},
  {key: 'trabajador', labelKey: 'admin.bateria.informe.plan.frenteTrabajador'},
  {key: 'urgente', labelKey: 'admin.bateria.informe.plan.frenteUrgente'},
  {key: 'monitoreo', labelKey: 'admin.bateria.informe.plan.frenteMonitoreo'},
]

function BloqueFactor({factor, testId}: {factor: FactorPlan; testId: string}) {
  const {t} = useTranslation()
  return (
    <div className="surface-divider border-b py-4 last:border-0" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground">
          {t('admin.bateria.formaValue', {forma: factor.forma})} · {factor.label}
        </p>
        {factor.nivel && (
          <Tag tone={NIVEL_TONE[factor.nivel] ?? 'neutral'}>{t(`admin.bateria.nivel.${factor.nivel}`)}</Tag>
        )}
      </div>
      {factor.segmento && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t('admin.bateria.informe.plan.foco', {segmento: factor.segmento})}
        </p>
      )}
      <p className="mt-2 text-sm text-foreground">
        <span className="font-medium">{t('admin.bateria.informe.plan.objetivo')}:</span> {factor.objetivo}
      </p>
      {FRENTES.map(({key, labelKey}) => {
        const lista = factor.acciones[key]
        if (lista.length === 0) return null
        return (
          <div key={key} className="mt-2">
            <p className="text-xs font-semibold text-foreground">{t(labelKey)}</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
              {lista.map((accion, i) => (
                <li key={i}>{accion}</li>
              ))}
            </ul>
          </div>
        )
      })}
      <p className="mt-2 text-xs text-muted-foreground">
        <span className="font-medium">{t('admin.bateria.informe.plan.protocolo')}:</span> {factor.protocolo}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        <span className="font-medium">{t('admin.bateria.informe.plan.criterio')}:</span> {factor.criterio}
      </p>
    </div>
  )
}

/** Bloque 8 (§P7): acciones de `bateria_plan_acciones.acciones_para`, la misma fuente del DOCX. Nunca
 * lleva marca de agua: el registro de validación solo habilita la línea de procedencia. */
export function PlanIntervencion({
  factoresPriorizados,
  factoresMonitoreo,
  estresPriorizado,
  planValidacionLine,
}: {
  factoresPriorizados: FactorPlan[]
  factoresMonitoreo: FactorPlan[]
  estresPriorizado: FactorPlan[]
  planValidacionLine: string | null
}) {
  const {t} = useTranslation()
  const sinPrioritarios = factoresPriorizados.length === 0 && estresPriorizado.length === 0

  return (
    <SectionCard
      title={t('admin.bateria.informe.plan.title')}
      subtitle={t('admin.bateria.informe.plan.subtitle')}
      actions={
        planValidacionLine ? (
          <Tag tone="green" data-testid="informe-plan-validado">
            {t('admin.bateria.informe.plan.validado')}
          </Tag>
        ) : (
          <Tag tone="amber" data-testid="informe-plan-pendiente">
            {t('admin.bateria.informe.plan.pendienteCertificacion')}
          </Tag>
        )
      }
      testId="informe-plan-intervencion">
      {planValidacionLine && (
        <p className="mb-3 text-xs text-muted-foreground" data-testid="informe-plan-validacion-linea">
          {planValidacionLine}
        </p>
      )}
      {sinPrioritarios ? (
        <p className="text-sm text-muted-foreground" data-testid="informe-plan-sin-prioritarios">
          {t('admin.bateria.informe.plan.sinPrioritarios')}
        </p>
      ) : (
        <div>
          {factoresPriorizados.map((f) => (
            <BloqueFactor
              key={`${f.forma}-${f.clave}`}
              factor={f}
              testId={`informe-plan-factor-${f.forma}-${f.clave}`}
            />
          ))}
          {estresPriorizado.map((f) => (
            <BloqueFactor key={`estres-${f.forma}`} factor={f} testId={`informe-plan-estres-${f.forma}`} />
          ))}
        </div>
      )}
      {factoresMonitoreo.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-foreground">{t('admin.bateria.informe.plan.monitoreoTitle')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('admin.bateria.informe.plan.monitoreoSubtitle')}</p>
          <ul className="mt-2 space-y-2" data-testid="informe-plan-monitoreo">
            {factoresMonitoreo.map((f) => (
              <li key={`${f.forma}-${f.clave}`} className="text-sm">
                <span className="font-medium text-foreground">
                  {t('admin.bateria.formaValue', {forma: f.forma})} · {f.label}.
                </span>{' '}
                <span className="text-muted-foreground">{f.acciones.monitoreo.join(' ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  )
}
